import {
  Box,
  Heading,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import moment from 'moment';
import PropTypes from 'prop-types';
import {
  computeRoasTargetCovenantSummary,
  formatRoasTargetCovenantSummaryMessage,
} from './roasTargetCovenantSummary';
import { roasTargetToCohortPeriod } from './roasTargetPeriods';
import { orderTearsheetExposureResultRows } from './tearsheetCovenantExposure';

const HIDDEN_CASHFLOW_METRIC_LABELS = new Set([
  'Max Exposure',
  '% Exposure To Cash',
  '% Exposure To Run Rate',
  '% Exposure To Runoff',
]);

const CASHFLOW_METRIC_ANNUAL_RUN_RATE_LABEL = 'Annual Run Rate';

const SCORECARD_ROW_NAMES_MONTHS = new Set([
  'Runoff (With Cash)',
  'Runoff (Without Cash)',
  'Runway',
  'Cash Balance',
  'CAC Payback',
]);

const SCORECARD_ROW_REVENUE_GROWTH = 'Revenue Growth (YoY)';

const SCORECARD_ROW_NAMES_STRING_VALUE = new Set([
  'ROAS Vector',
  'Investors',
  'Management',
]);

const isUaPredictionScorecardRow = (row) =>
  row?.key === 'ua_prediction' || row?.key === 'cac_prediction';

const formatCacPredictionRatioPercent = (detail, row) => {
  let ratio = null;
  if (detail?.ratio != null && Number.isFinite(Number(detail.ratio))) {
    ratio = Number(detail.ratio);
  } else if (row?.valueText != null && row.valueText !== '') {
    const parsed = Number(String(row.valueText).replace(/,/g, ''));
    if (Number.isFinite(parsed)) {
      ratio = parsed;
    }
  }
  if (ratio == null) {
    return '—';
  }
  const pct = ratio * 100;
  return `${formatScorecardNumberWithDots(Math.round(pct * 100) / 100)}%`;
};

const formatCacPredictionR2 = (r2) =>
  r2 != null && Number.isFinite(Number(r2)) ? Number(r2).toFixed(4) : '—';

const CacPredictionRegressionBlock = ({
  title,
  modelLabel,
  equation,
  r2,
  minUaDisplay,
  targetMob,
}) => (
  <Box>
    {title ? (
      <Text fontWeight={'semibold'} fontSize={'sm'} mb={2}>
        {title}
      </Text>
    ) : null}
    {modelLabel ? (
      <Text mb={1} fontSize={'sm'}>
        <Text as={'span'} fontWeight={'semibold'}>
          Model:{' '}
        </Text>
        {modelLabel}
      </Text>
    ) : null}
    <Text mb={1} fontSize={'sm'}>
      <Text as={'span'} fontWeight={'semibold'}>
        R²:{' '}
      </Text>
      {formatCacPredictionR2(r2)}
    </Text>
    <Text mb={1} fontSize={'sm'}>
      <Text as={'span'} fontWeight={'semibold'}>
        Equation:{' '}
      </Text>
      {equation || '—'}
    </Text>
    <Text fontSize={'sm'}>
      <Text as={'span'} fontWeight={'semibold'}>
        {`Max marketing @ ≥100% ROAS (MOB ${targetMob ?? 12}): `}
      </Text>
      {minUaDisplay || '—'}
    </Text>
  </Box>
);

const CacPredictionRegressionDetailPanel = ({ detail }) => {
  if (!detail) {
    return null;
  }
  const mob = detail.targetMob ?? 12;
  const bestIsLinear = detail.regressionType === 'linear';
  const showLinearSeparately =
    detail.linear && !bestIsLinear && detail.linear.equation;

  return (
    <Box>
      <CacPredictionRegressionBlock
        title={
          showLinearSeparately
            ? `Best fit (${detail.regressionTypeLabel || detail.regressionType || '—'})`
            : null
        }
        modelLabel={
          showLinearSeparately
            ? null
            : detail.regressionTypeLabel || detail.regressionType || '—'
        }
        equation={detail.equation}
        r2={detail.r2}
        minUaDisplay={detail.minUaFor100RoasDisplay}
        targetMob={mob}
      />
      {showLinearSeparately ? (
        <Box mt={4} pt={4} borderTopWidth={'1px'} borderColor={'gray.200'}>
          <CacPredictionRegressionBlock
            title={'Linear regression'}
            modelLabel={'Linear'}
            equation={detail.linear.equation}
            r2={detail.linear.r2}
            minUaDisplay={detail.linear.minUaFor100RoasDisplay}
            targetMob={mob}
          />
        </Box>
      ) : null}
    </Box>
  );
};

CacPredictionRegressionDetailPanel.propTypes = {
  detail: PropTypes.object,
};

const formatScorecardNumberWithDots = (numericValue) =>
  new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 20,
  }).format(numericValue);

const isInvestorOrManagementScorecardRow = (row) =>
  row.key === 'investors' || row.key === 'management';

const formatInvestorManagementScorecardValue = (row) => {
  if (
    row.overrideActive &&
    row.overrideNumericValue != null &&
    Number.isFinite(Number(row.overrideNumericValue))
  ) {
    return String(Math.round(Number(row.overrideNumericValue)));
  }
  if (
    row.score != null &&
    Number.isInteger(Number(row.score)) &&
    Number(row.score) >= 1 &&
    Number(row.score) <= 3
  ) {
    return String(Number(row.score));
  }
  const trimmed = String(row.valueText ?? '').trim();
  if (/^[123]$/.test(trimmed)) {
    return trimmed;
  }
  return '—';
};

const formatScorecardOverrideNumericForRow = (row, numericValue) => {
  const name = row.name;
  const n = Number(numericValue);
  if (row.key === 'roas_vector' || name === 'ROAS Vector') {
    const months = Math.round(n);
    if (months === 1) {
      return '1 month';
    }
    return `${months} months`;
  }
  if (row.key === 'investors' || row.key === 'management') {
    return String(Math.round(n));
  }
  const core = formatScorecardNumberWithDots(n);
  if (
    row.key === 'revenue_growth_yoy' ||
    row.unit === '% YoY' ||
    name === SCORECARD_ROW_REVENUE_GROWTH
  ) {
    const s = String(core).trim();
    if (s.endsWith('%')) {
      return s;
    }
    return `${s} %`;
  }
  const unitLc = String(row.unit || '').toLowerCase();
  const isOpexMultiple =
    unitLc.includes('opex') && (unitLc.includes('x') || unitLc.includes('monthly'));
  if (isOpexMultiple && core !== '—' && core !== '-') {
    return `${core}x`;
  }
  if (
    isUaPredictionScorecardRow(row) ||
    String(row.unit || '').toLowerCase().includes('funding marketing')
  ) {
    return `${formatScorecardNumberWithDots(Math.round(n * 100 * 100) / 100)}%`;
  }
  if (
    row.unit === 'months' ||
    String(row.unit || '').includes('months') ||
    SCORECARD_ROW_NAMES_MONTHS.has(name)
  ) {
    if (/months?$/i.test(String(core))) {
      return core;
    }
    return `${core} months`;
  }
  return core;
};

const finiteNumberOrNull = (value) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const formatNumberWithDots = (numericValue) =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numericValue);

const formatCashflowCurrencyWholeDollars = (numericValue) =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numericValue);

const formatCashflowNumericDisplayText = (valueText, options = {}) => {
  const { currency = false } = options;
  if (valueText == null || valueText === '') {
    return '—';
  }
  const s = String(valueText).trim();
  if (s === '—' || s === '-') {
    return s;
  }
  const asterisk = s.endsWith('*');
  const core = asterisk ? s.slice(0, -1).trim() : s;
  if (/MOB\s/i.test(core) || /[()]/.test(core)) {
    return s;
  }
  const num = Number(core.replace(/[$,]/g, ''));
  if (!Number.isFinite(num)) {
    return s;
  }
  const formatted = currency
    ? formatCashflowCurrencyWholeDollars(num)
    : formatNumberWithDots(num);
  if (!currency) {
    return asterisk ? `${formatted}*` : formatted;
  }
  const dollars = `$${formatted}`;
  return asterisk ? `${dollars}*` : dollars;
};

const cashflowMetricLabelIsCurrency = (label) => {
  if (!label) {
    return false;
  }
  return (
    /Runoff Revenue/i.test(label) ||
    /Runway Revenue/i.test(label) ||
    label === CASHFLOW_METRIC_ANNUAL_RUN_RATE_LABEL ||
    label === 'Max Exposure' ||
    /Gross revenue/i.test(label)
  );
};

const formatScorecardValueCell = (row) => {
  const name = row.name;
  const raw = row.valueText ?? '';

  if (isInvestorOrManagementScorecardRow(row)) {
    return formatInvestorManagementScorecardValue(row);
  }

  if (
    row.overrideActive &&
    row.overrideNumericValue != null &&
    Number.isFinite(Number(row.overrideNumericValue))
  ) {
    return formatScorecardOverrideNumericForRow(
      row,
      Number(row.overrideNumericValue)
    );
  }

  if (row.kind === 'qualitative' || SCORECARD_ROW_NAMES_STRING_VALUE.has(name)) {
    const t = String(raw).trim();
    return t === '' ? '—' : t;
  }

  let core = formatCashflowNumericDisplayText(raw);

  if (core === '—' || core === '-' || core === '') {
    return '—';
  }

  if (
    row.key === 'revenue_growth_yoy' ||
    row.unit === '% YoY' ||
    name === SCORECARD_ROW_REVENUE_GROWTH
  ) {
    const s = String(core).trim();
    if (s.endsWith('%')) {
      return s;
    }
    return `${s} %`;
  }

  const unitLc = String(row.unit || '').toLowerCase();
  const isOpexMultiple =
    unitLc.includes('opex') && (unitLc.includes('x') || unitLc.includes('monthly'));
  if (isOpexMultiple && core !== '—' && core !== '-') {
    return `${core}x`;
  }

  if (
    isUaPredictionScorecardRow(row) ||
    String(row.unit || '').toLowerCase().includes('funding marketing')
  ) {
    if (core === '—' || core === '-') {
      return '—';
    }
    const ratio = Number(String(core).replace(/,/g, ''));
    if (!Number.isFinite(ratio)) {
      return '—';
    }
    return `${formatScorecardNumberWithDots(Math.round(ratio * 100 * 100) / 100)}%`;
  }

  if (row.unit === 'months' || SCORECARD_ROW_NAMES_MONTHS.has(name)) {
    if (/months?$/i.test(String(core))) {
      return core;
    }
    return `${core} months`;
  }

  if (String(row.unit || '').includes('months') || row.key === 'roas_vector') {
    if (/months?$/i.test(String(core))) {
      return core;
    }
    return `${core} months`;
  }

  return core;
};

const formatScoreDisplay = (row) => {
  const s = row.score;
  if (s != null && s !== '') {
    return String(s);
  }
  return '—';
};

const formatResultValue = (resultRow) => {
  if (resultRow.value === null || resultRow.value === undefined) {
    return '-';
  }

  const isPercentageValue =
    resultRow.kpi?.includes('%') || resultRow.kpi?.includes('MoM');
  const isCurrencyValue =
    resultRow.kpi?.includes('Funding') ||
    resultRow.kpi?.includes('Cash Balance') ||
    resultRow.kpi?.includes('Exposure') ||
    resultRow.kpi?.includes('Repayment');
  const formattedValue = formatNumberWithDots(Number(resultRow.value));

  if (isPercentageValue) {
    return `${formattedValue}%`;
  }
  if (isCurrencyValue) {
    return `$${formattedValue}`;
  }
  return formattedValue;
};

const formatConstraintValue = (constraintText) => {
  if (!constraintText) {
    return '-';
  }
  const shouldInterpretAsPercentDecimal = constraintText.includes('%');
  return constraintText.replace(/-?\d+(?:\.\d+)?/g, (numericPart) => {
    let numericValue = Number(numericPart);
    if (
      shouldInterpretAsPercentDecimal &&
      Number.isFinite(numericValue) &&
      Math.abs(numericValue) > 0 &&
      Math.abs(numericValue) < 1
    ) {
      numericValue *= 100;
    }
    return formatNumberWithDots(numericValue);
  });
};

const getCheckDisplay = (checkStatus) => {
  if (checkStatus === 'pass') {
    return { label: 'OK', color: 'green.500' };
  }
  if (checkStatus === 'fail') {
    return { label: 'Failed', color: 'red.500' };
  }
  return null;
};

const orderResultRowsForPdf = (resultRows) =>
  orderTearsheetExposureResultRows(resultRows);

const buildCashflowMetricsRowsForPdf = (payload) => {
  const raw = (payload.cashflowMetrics ?? []).filter(
    (row) => row && !HIDDEN_CASHFLOW_METRIC_LABELS.has(row.label)
  );
  const annual = finiteNumberOrNull(payload.annualRunRate);
  const out = [];
  let runwaySeen = false;
  let insertedAnnual = false;
  for (const row of raw) {
    if (row.label === 'Duration' && !insertedAnnual && runwaySeen) {
      if (annual != null && Number.isFinite(annual)) {
        out.push({
          label: CASHFLOW_METRIC_ANNUAL_RUN_RATE_LABEL,
          valueText: String(annual),
        });
        insertedAnnual = true;
      }
    }
    out.push(row);
    if (row.label === 'Runway Revenue') {
      runwaySeen = true;
    }
  }
  return out;
};

const scorecardFinalBandFromPayload = (payload) => {
  const rows = payload.cashflowScorecard ?? [];
  const finalBands = payload.scorecardGradeThresholds?.finalScore;
  const scored = rows.filter(
    (r) => r.score != null && Number.isInteger(Number(r.score))
  );
  if (scored.length === 0) {
    return payload.scorecardFinalScore ?? null;
  }
  const avg =
    scored.reduce((sum, r) => sum + Number(r.score), 0) / scored.length;
  const minA = finalBands?.minAverageForGradeA ?? 2.5;
  const minB = finalBands?.minAverageForGradeB ?? 1.5;
  if (!Number.isFinite(avg)) {
    return null;
  }
  if (avg >= minA) {
    return 3;
  }
  if (avg >= minB) {
    return 2;
  }
  return 1;
};

/** Mean of integer scorecard row scores (same basis as TearsheetNew when rows are scored). */
const scorecardFinalMeanFromPayload = (payload) => {
  const rows = payload.cashflowScorecard ?? [];
  const scored = rows.filter(
    (r) => r.score != null && Number.isInteger(Number(r.score))
  );
  if (scored.length === 0) {
    return null;
  }
  const avg =
    scored.reduce((sum, r) => sum + Number(r.score), 0) / scored.length;
  return Number.isFinite(avg) ? avg : null;
};

const finalScoreLetterFromBand = (band) => {
  if (band === 3) {
    return 'A';
  }
  if (band === 2) {
    return 'B';
  }
  if (band === 1) {
    return 'C';
  }
  return null;
};

const exposureToRunoffThresholdPctFromPayload = (payload) => {
  const band = scorecardFinalBandFromPayload(payload);
  const raw = payload?.scorecardGradeThresholds?.exposureToRunOff;
  if (band === 3) {
    return raw?.grade3 ?? 80;
  }
  if (band === 2) {
    return raw?.grade2 ?? 50;
  }
  return raw?.grade1 ?? 25;
};

const exposureToRunRateThresholdPctFromPayload = (payload) => {
  const band = scorecardFinalBandFromPayload(payload);
  const raw = payload?.scorecardGradeThresholds?.exposureToRunRate;
  if (band === 3) {
    return raw?.grade3 ?? 40;
  }
  if (band === 2) {
    return raw?.grade2 ?? 20;
  }
  return raw?.grade1 ?? 10;
};

/** Same formula as TearsheetNew Final Score KPI "Exposure to Runoff (With Cash)". */
const exposureToRunoffWithCashPctFromPayload = (payload) => {
  const runoffRevenueMetric = (payload.cashflowMetrics ?? []).find(
    (m) => m.label === 'Runoff Revenue (Calculated with Cash)'
  );
  const runoffRevenueParsed = runoffRevenueMetric?.valueText
    ? parseFloat(String(runoffRevenueMetric.valueText).replace(/,/g, ''))
    : null;
  const runoffRevenue =
    runoffRevenueParsed != null && Number.isFinite(runoffRevenueParsed)
      ? runoffRevenueParsed
      : null;
  const futureExposure = payload.futureExposure ?? null;
  if (
    runoffRevenue == null ||
    futureExposure == null ||
    runoffRevenue === 0
  ) {
    return null;
  }
  return (
    Math.round((futureExposure / runoffRevenue) * 100 * 100) / 100
  );
};

/**
 * Static layout for react-to-pdf from a saved V2 tearsheet payload (`payloadJson`).
 */
const TearsheetNewPdfDocument = ({ payload, savedAt }) => {
  const roasView = payload.roasGrossNetView === 'net' ? 'net' : 'gross';
  const orderedResultRows = orderResultRowsForPdf(payload.resultRows);
  const metricsRows = buildCashflowMetricsRowsForPdf(payload);
  const finalMean = scorecardFinalMeanFromPayload(payload);
  const finalLetter = finalScoreLetterFromBand(
    scorecardFinalBandFromPayload(payload)
  );
  const savedDateLabel = savedAt
    ? moment(savedAt).format('MMM D, YYYY h:mm A')
    : null;
  const roasTargetCovenantSummary = computeRoasTargetCovenantSummary({
    roasTargets: payload.roasTargets,
    roasCohort: payload.roasCohort,
  });
  const roasTargetCovenantMessage = formatRoasTargetCovenantSummaryMessage(
    roasTargetCovenantSummary
  );

  return (
    <Box
      data-tearsheet-new-pdf-root
      maxW={'800px'}
      p={6}
      bg={'white'}
      color={'gray.800'}
      fontSize={'sm'}
    >
      <Heading size={'md'} mb={2}>
        Tearsheet
      </Heading>
      <Text fontWeight={'bold'} fontSize={'lg'} mb={1}>
        {payload.name || 'Untitled'}
      </Text>
      {savedDateLabel ? (
        <Text fontSize={'xs'} color={'gray.600'} mb={4}>
          Saved {savedDateLabel}
        </Text>
      ) : (
        <Box mb={4} />
      )}

      <Box borderWidth={'1px'} borderColor={'gray.200'} borderRadius={'md'} p={4} mb={6}>
        <Text fontWeight={'semibold'} mb={2}>
          Summary
        </Text>
        <Table size={'sm'} variant={'simple'}>
          <Tbody>
            {(() => {
              if (finalLetter == null && finalMean == null) {
                return null;
              }
              const parts = [];
              if (finalLetter != null) {
                parts.push(`Grade ${finalLetter}`);
              }
              if (finalMean != null) {
                parts.push(
                  finalMean.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                );
              }
              const cell = parts.join(' · ');
              if (!cell) {
                return null;
              }
              return (
                <Tr>
                  <Td fontWeight={'medium'}>Final score</Td>
                  <Td>{cell}</Td>
                </Tr>
              );
            })()}
            <Tr>
              <Td fontWeight={'medium'}>Mode</Td>
              <Td>{payload.mode === 'installment' ? 'Installment' : 'Deal'}</Td>
            </Tr>
            {payload.dealId != null && String(payload.dealId) !== '' ? (
              <Tr>
                <Td fontWeight={'medium'}>Deal ID</Td>
                <Td>{String(payload.dealId)}</Td>
              </Tr>
            ) : null}
            {payload.installmentId != null && String(payload.installmentId) !== '' ? (
              <Tr>
                <Td fontWeight={'medium'}>Installment ID</Td>
                <Td>{String(payload.installmentId)}</Td>
              </Tr>
            ) : null}
            <Tr>
              <Td fontWeight={'medium'}>Requested funding</Td>
              <Td>
                ${formatNumberWithDots(Number(payload.requestedFundingAmount || 0))}
                {payload.requestedFundingPct != null
                  ? ` (${formatNumberWithDots(Number(payload.requestedFundingPct))}%)`
                  : ''}
              </Td>
            </Tr>
            <Tr>
              <Td fontWeight={'medium'}>Marketing spend</Td>
              <Td>${formatNumberWithDots(Number(payload.marketingSpendAmount || 0))}</Td>
            </Tr>
            <Tr>
              <Td fontWeight={'medium'}>Cash balance</Td>
              <Td>
                ${formatNumberWithDots(Number(payload.cashBalanceAmount || 0))}
                {payload.cashBalanceAsOfDate
                  ? ` (as of ${payload.cashBalanceAsOfDate})`
                  : ''}
              </Td>
            </Tr>
            {(() => {
              const rawPct = payload.exposureToRunRatePct;
              const actual =
                rawPct != null && Number.isFinite(Number(rawPct))
                  ? Number(rawPct)
                  : null;
              const thr = exposureToRunRateThresholdPctFromPayload(payload);
              if (actual == null && thr == null) {
                return null;
              }
              return (
                <Tr>
                  <Td fontWeight={'medium'}>Exposure to run rate</Td>
                  <Td>
                    {actual != null
                      ? `${formatNumberWithDots(actual)}%`
                      : '—'}
                    {thr != null ? ` (max ${formatNumberWithDots(thr)}%)` : ''}
                  </Td>
                </Tr>
              );
            })()}
            {(() => {
              const actual = exposureToRunoffWithCashPctFromPayload(payload);
              const thr = exposureToRunoffThresholdPctFromPayload(payload);
              if (actual == null && thr == null) {
                return null;
              }
              return (
                <Tr>
                  <Td fontWeight={'medium'}>
                    Exposure to Runoff (With Cash)
                  </Td>
                  <Td>
                    {actual != null
                      ? `${formatNumberWithDots(actual)}%`
                      : '—'}
                    {thr != null ? ` (max ${formatNumberWithDots(thr)}%)` : ''}
                  </Td>
                </Tr>
              );
            })()}
            <Tr>
              <Td fontWeight={'medium'}>ROAS cohort view</Td>
              <Td>{roasView === 'net' ? 'Net' : 'Gross'}</Td>
            </Tr>
            {payload.scorecardConfigVersion != null ? (
              <Tr>
                <Td fontWeight={'medium'}>Scorecard config</Td>
                <Td>v{String(payload.scorecardConfigVersion)}</Td>
              </Tr>
            ) : null}
          </Tbody>
        </Table>
      </Box>

      {roasTargetCovenantMessage ? (
        <Box
          mb={6}
          p={3}
          borderWidth={'1px'}
          borderColor={'gray.200'}
          borderRadius={'md'}
          bg={'gray.50'}
        >
          <Text fontSize={'sm'}>{roasTargetCovenantMessage}</Text>
        </Box>
      ) : null}

      {(payload.cashflowScorecard ?? []).length > 0 ? (
        <Box mb={6}>
          <Heading size={'sm'} mb={2}>
            Scorecard
          </Heading>
          <Table size={'sm'}>
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th isNumeric>Value</Th>
                <Th isNumeric>Score</Th>
              </Tr>
            </Thead>
            <Tbody>
              {(payload.cashflowScorecard ?? []).map((row, index) => (
                <Tr key={row.key != null ? String(row.key) : `${row.name}-${index}`}>
                  <Td>{row.name}</Td>
                  <Td isNumeric>
                    {isUaPredictionScorecardRow(row)
                      ? formatCacPredictionRatioPercent(
                          payload.cacPrediction,
                          row
                        )
                      : formatScorecardValueCell(row)}
                  </Td>
                  <Td isNumeric>
                    {formatScoreDisplay(row)}
                    {row.overrideActive ? ' *' : ''}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      ) : null}

      {payload.cacPrediction ? (
        <Box mb={6}>
          <Heading size={'sm'} mb={2}>
            UA Prediction
          </Heading>
          <CacPredictionRegressionDetailPanel detail={payload.cacPrediction} />
        </Box>
      ) : null}

      {orderedResultRows.length > 0 ? (
        <Box
          mb={6}
          style={{
            pageBreakBefore: 'always',
            breakBefore: 'page',
          }}
        >
          <Heading size={'sm'} mb={2}>
            Covenants
          </Heading>
          <Table size={'sm'}>
            <Thead>
              <Tr>
                <Th>Section</Th>
                <Th>KPI</Th>
                <Th>Value</Th>
                <Th>Constraint</Th>
                <Th>Date</Th>
                <Th>Check</Th>
              </Tr>
            </Thead>
            <Tbody>
              {orderedResultRows.map((row, index) => {
                const checkDisplay = getCheckDisplay(row.check);
                return (
                  <Tr key={index}>
                    <Td>{row.area}</Td>
                    <Td>{row.kpi}</Td>
                    <Td>{formatResultValue(row)}</Td>
                    <Td>{formatConstraintValue(row.constraint)}</Td>
                    <Td>{row.date || '-'}</Td>
                    <Td>
                      {checkDisplay ? (
                        <Text color={checkDisplay.color} fontWeight={'bold'}>
                          {checkDisplay.label}
                        </Text>
                      ) : (
                        '-'
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      ) : null}

      {(payload.roasTargets ?? []).length > 0 ? (
        <Box mb={6}>
          <Heading size={'sm'} mb={2}>
            ROAS covenant targets
          </Heading>
          <Text fontSize={'xs'} color={'gray.600'} mb={2}>
            Period labels match the cohort performance grid (P0 = MOB 1).
          </Text>
          <Table size={'sm'}>
            <Thead>
              <Tr>
                <Th>Period</Th>
                <Th isNumeric>Min ROAS</Th>
              </Tr>
            </Thead>
            <Tbody>
              {(payload.roasTargets ?? []).map((target) => {
                const cohortPeriod = roasTargetToCohortPeriod(target.period);
                return (
                  <Tr key={target.period}>
                    <Td>
                      {cohortPeriod != null ? `P${cohortPeriod}` : `P${target.period}`}
                    </Td>
                    <Td isNumeric>
                      ≥{formatNumberWithDots(Number(target.value || 0) * 100)}%
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      ) : null}

      {metricsRows.length > 0 ? (
        <Box mb={6}>
          <Heading size={'sm'} mb={2}>
            Cashflow metrics
          </Heading>
          <Table size={'sm'}>
            <Thead>
              <Tr>
                <Th>KPI</Th>
                <Th isNumeric>Value</Th>
              </Tr>
            </Thead>
            <Tbody>
              {metricsRows.map((row, index) => (
                <Tr key={`${row.label}-${index}`}>
                  <Td>{row.label}</Td>
                  <Td isNumeric>
                    {formatCashflowNumericDisplayText(row.valueText, {
                      currency: cashflowMetricLabelIsCurrency(row.label),
                    })}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      ) : null}

      {payload.dealConstraints != null &&
      typeof payload.dealConstraints === 'object' &&
      Object.keys(payload.dealConstraints).length > 0 ? (
        <Box mb={6}>
          <Heading size={'sm'} mb={2}>
            Deal constraints (raw)
          </Heading>
          <Text
            as={'pre'}
            fontSize={'xs'}
            whiteSpace={'pre-wrap'}
            wordBreak={'break-word'}
            p={2}
            bg={'gray.50'}
            borderRadius={'md'}
          >
            {JSON.stringify(payload.dealConstraints, null, 2)}
          </Text>
        </Box>
      ) : null}
    </Box>
  );
};

TearsheetNewPdfDocument.propTypes = {
  payload: PropTypes.object.isRequired,
  savedAt: PropTypes.string,
};

TearsheetNewPdfDocument.defaultProps = {
  savedAt: null,
};

export default TearsheetNewPdfDocument;
